import React, { useEffect, useState } from 'react';
import { AdminLayout } from '@/components/AdminLayout';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Pencil, Trash2, Save } from 'lucide-react';
import { toast } from 'sonner';
import { ZoneEditor } from '@/components/ZoneEditor';
import { eur } from '@/lib/format';

const PALETTE = ['#1F6F5B', '#FF7A59', '#2E7BCF', '#DFA43A', '#9B5DE5', '#00BBF9'];

export default function AdminZones() {
  const [zones, setZones] = useState([]);
  const [settings, setSettings] = useState(null);
  const [pendingPolygon, setPendingPolygon] = useState(null);
  const [edit, setEdit] = useState(null);

  const load = () => {
    api.get('/zones?active_only=false').then(r => setZones(r.data));
    api.get('/settings').then(r => setSettings(r.data));
  };

  useEffect(() => { load(); }, []);

  const onCreate = (polygon) => {
    setPendingPolygon(polygon);
    setEdit({ name: '', price: 3.00, color: PALETTE[0], active: true, polygon });
  };

  const save = async () => {
    try {
      if (edit.id) {
        await api.patch(`/admin/zones/${edit.id}`, edit);
      } else {
        await api.post('/admin/zones', edit);
      }
      toast.success('Zona salvata');
      setEdit(null); setPendingPolygon(null); load();
    } catch (e) { toast.error('Errore salvataggio'); }
  };

  const del = async (id) => {
    if (!window.confirm('Eliminare questa zona?')) return;
    await api.delete(`/admin/zones/${id}`);
    toast.success('Zona eliminata'); load();
  };

  return (
    <AdminLayout testid="admin-zones-page">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-widest text-[--brand-muted]">Zone di consegna</div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold">Disegna zone e imposta prezzi</h1>
        <p className="text-sm text-[--brand-muted] mt-1">Usa lo strumento poligono in alto a destra sulla mappa per disegnare una nuova zona.</p>
      </div>

      <div className="grid gap-6 lg:grid-cols-12">
        <div className="lg:col-span-8" data-testid="admin-zones-map">
          <ZoneEditor
            zones={zones}
            onCreate={onCreate}
            onEdit={(z) => setEdit(z)}
            center={settings ? [settings.lat, settings.lng] : [45.4642, 9.19]}
          />
        </div>

        <div className="lg:col-span-4" data-testid="admin-zones-list">
          <div className="rounded-2xl bg-white border border-[--brand-border] p-4 shadow-[var(--shadow-sm)]">
            <div className="font-display font-bold mb-3">Zone ({zones.length})</div>
            {zones.length === 0 && (
              <div className="text-sm text-[--brand-muted]">Nessuna zona. Disegna la prima!</div>
            )}
            <div className="space-y-2">
              {zones.map(z => (
                <div key={z.id} className="rounded-xl border border-[--brand-border] p-3 flex items-center gap-3">
                  <div className="w-4 h-4 rounded" style={{ background: z.color }}/>
                  <div className="flex-1">
                    <div className="font-semibold text-sm">{z.name}</div>
                    <div className="text-xs text-[--brand-muted]">{eur(z.price)}{!z.active && <span className="ml-2 text-[--brand-danger]">disattiva</span>}</div>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => setEdit(z)}><Pencil size={14}/></Button>
                  <Button size="sm" variant="ghost" onClick={() => del(z.id)}><Trash2 size={14} className="text-[--brand-danger]"/></Button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <Dialog open={!!edit} onOpenChange={(o) => { if (!o) { setEdit(null); setPendingPolygon(null); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{edit?.id ? 'Modifica zona' : 'Nuova zona'}</DialogTitle></DialogHeader>
          {edit && (
            <div className="space-y-3">
              <div><Label>Nome zona</Label><Input value={edit.name} onChange={e => setEdit({...edit, name: e.target.value})} placeholder="Es. Centro" className="rounded-xl" data-testid="zones-zone-name-input"/></div>
              <div><Label>Prezzo consegna (€)</Label><Input type="number" step="0.10" value={edit.price} onChange={e => setEdit({...edit, price: parseFloat(e.target.value) || 0})} className="rounded-xl" data-testid="zones-zone-price-input"/></div>
              <div>
                <Label>Colore</Label>
                <div className="flex gap-2 mt-1">
                  {PALETTE.map(c => (
                    <button key={c} type="button" onClick={() => setEdit({...edit, color: c})}
                            className={`w-8 h-8 rounded-full border-2 ${edit.color === c ? 'border-black' : 'border-transparent'}`}
                            style={{ background: c }}/>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-2"><Switch checked={edit.active} onCheckedChange={v => setEdit({...edit, active: v})}/><Label>Zona attiva</Label></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="secondary" onClick={() => { setEdit(null); setPendingPolygon(null); }}>Annulla</Button>
            <Button onClick={save} className="bg-[--brand-primary]" data-testid="zones-save-zone-button"><Save size={14} className="mr-1"/>Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
